// src/components/pages/WorkspaceBranding.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import {
  getWorkspaceBranding, saveWorkspaceBranding, uploadWorkspaceLogo,
  COLOR_PRESETS_BRAND, generateBrandingCSS
} from '../../services/brandingService';
import toast from 'react-hot-toast';
import {
  FiLoader, FiUpload, FiCheck, FiImage, FiDroplet,
  FiGlobe, FiEye, FiX, FiLayers
} from 'react-icons/fi';

const WorkspaceBranding = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [branding, setBranding] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploading, setUploading] = useState(false);
  const logoInputRef = useRef(null);
  const logoSmallInputRef = useRef(null);

  const workspaceId = user?.workspaceId || user?.uid;

  useEffect(() => {
    const load = async () => {
      if (!workspaceId) return;
      try {
        const b = await getWorkspaceBranding(workspaceId);
        setBranding(b);
      } catch (err) {
        console.error('Error loading branding:', err);
      }
      setLoading(false);
    };
    load();
  }, [workspaceId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveWorkspaceBranding(workspaceId, branding);
      toast.success('Personalizacion guardada');
    } catch (err) {
      toast.error('Error al guardar');
      console.error(err);
    }
    setSaving(false);
  };

  const handleLogoUpload = async (e, type = 'logo') => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('El archivo debe ser menor a 2MB');
      return;
    }
    setUploading(true);
    try {
      const url = await uploadWorkspaceLogo(workspaceId, file, type);
      setBranding(prev => ({ ...prev, [type]: url }));
      toast.success('Logo subido');
    } catch (err) {
      toast.error('Error al subir logo');
    }
    setUploading(false);
  };

  const handlePresetSelect = (preset) => {
    setBranding(prev => ({
      ...prev,
      primaryColor: preset.primary,
      accentColor: preset.accent,
      sidebarColor: preset.sidebar,
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FiLoader className="animate-spin text-indigo-500" size={24} />
      </div>
    );
  }

  if (!branding) return null;

  const cssVars = generateBrandingCSS(branding);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Personalizar marca</h2>
          <p className="text-sm text-gray-500">Logo, colores y dominio de tu empresa</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1.5">
            <FiEye size={14} /> {showPreview ? 'Ocultar' : 'Vista previa'}
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-1.5">
            {saving ? <FiLoader className="animate-spin" size={14} /> : <FiCheck size={14} />}
            Guardar
          </button>
        </div>
      </div>

      {/* Preview */}
      {showPreview && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-lg" style={cssVars}>
          <div className="flex h-48">
            {/* Sidebar preview */}
            <div className="w-48 flex-shrink-0 p-4 flex flex-col gap-3" style={{ backgroundColor: branding.sidebarColor }}>
              <div className="flex items-center gap-2">
                {branding.logoSmall ? (
                  <img src={branding.logoSmall} alt="" className="w-8 h-8 rounded-lg object-contain bg-white/10" />
                ) : (
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: branding.primaryColor }}>
                    <FiLayers size={14} className="text-white" />
                  </div>
                )}
                <span className="text-white text-xs font-bold truncate">{branding.companyName || 'Mi Empresa'}</span>
              </div>
              {['Dashboard', 'Tareas', 'Calendario'].map(item => (
                <div key={item} className="px-3 py-1.5 rounded-lg text-white/60 text-[10px] hover:bg-white/10">{item}</div>
              ))}
            </div>
            {/* Main preview */}
            <div className="flex-1 p-4 bg-gray-50 dark:bg-gray-900">
              <div className="flex items-center gap-3 mb-4">
                {branding.logo ? (
                  <img src={branding.logo} alt="" className="h-8 object-contain" />
                ) : (
                  <div className="h-8 px-3 rounded-lg flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: branding.primaryColor }}>
                    {branding.companyName || 'Mi Empresa'}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="h-3 rounded-full w-3/4" style={{ backgroundColor: branding.primaryColor, opacity: 0.2 }} />
                <div className="h-3 rounded-full w-1/2" style={{ backgroundColor: branding.accentColor, opacity: 0.15 }} />
                <button className="mt-2 px-4 py-1.5 rounded-lg text-white text-[10px] font-bold" style={{ backgroundColor: branding.primaryColor }}>
                  Boton de ejemplo
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logos */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm space-y-5">
          <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <FiImage size={16} className="text-indigo-500" /> Logos
          </h3>

          {/* Logo principal */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Logo principal (header, facturas)</label>
            <div className="flex items-center gap-4">
              <div className="w-32 h-16 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-900">
                {branding.logo ? (
                  <img src={branding.logo} alt="Logo" className="max-w-full max-h-full object-contain p-1" />
                ) : (
                  <FiImage size={20} className="text-gray-300" />
                )}
              </div>
              <div className="space-y-2">
                <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e, 'logo')} />
                <button onClick={() => logoInputRef.current?.click()} disabled={uploading}
                  className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-100 disabled:opacity-50 flex items-center gap-1">
                  {uploading ? <FiLoader className="animate-spin" size={12} /> : <FiUpload size={12} />} Subir logo
                </button>
                {branding.logo && (
                  <button onClick={() => setBranding(prev => ({ ...prev, logo: '' }))} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5">
                    <FiX size={10} /> Quitar
                  </button>
                )}
                <p className="text-[10px] text-gray-400">PNG o SVG, max 2MB</p>
              </div>
            </div>
          </div>

          {/* Logo pequeno (icono) */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Icono (sidebar, favicon)</label>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden bg-gray-50 dark:bg-gray-900">
                {branding.logoSmall ? (
                  <img src={branding.logoSmall} alt="Icon" className="max-w-full max-h-full object-contain p-0.5" />
                ) : (
                  <FiImage size={16} className="text-gray-300" />
                )}
              </div>
              <div className="space-y-2">
                <input ref={logoSmallInputRef} type="file" accept="image/*" className="hidden" onChange={e => handleLogoUpload(e, 'logoSmall')} />
                <button onClick={() => logoSmallInputRef.current?.click()} disabled={uploading}
                  className="px-3 py-1.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-lg hover:bg-indigo-100 disabled:opacity-50 flex items-center gap-1">
                  {uploading ? <FiLoader className="animate-spin" size={12} /> : <FiUpload size={12} />} Subir icono
                </button>
                {branding.logoSmall && (
                  <button onClick={() => setBranding(prev => ({ ...prev, logoSmall: '' }))} className="text-[10px] text-red-500 hover:text-red-700 flex items-center gap-0.5">
                    <FiX size={10} /> Quitar
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Colors */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm space-y-5">
          <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2">
            <FiDroplet size={16} className="text-purple-500" /> Colores
          </h3>

          {/* Color presets */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-2">Temas predefinidos</label>
            <div className="grid grid-cols-5 gap-2">
              {COLOR_PRESETS_BRAND.map(preset => (
                <button
                  key={preset.name}
                  onClick={() => handlePresetSelect(preset)}
                  className={`group relative flex flex-col items-center gap-1 p-2 rounded-xl border transition-all hover:shadow-md ${
                    branding.primaryColor === preset.primary
                      ? 'border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50/50'
                      : 'border-gray-100 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="flex gap-0.5">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.accent }} />
                  </div>
                  <span className="text-[9px] text-gray-500">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom colors */}
          <div className="space-y-3">
            {[
              { key: 'primaryColor', label: 'Color primario', desc: 'Botones, links, acentos' },
              { key: 'accentColor', label: 'Color secundario', desc: 'Hover, badges, detalles' },
              { key: 'sidebarColor', label: 'Color sidebar', desc: 'Fondo del menu lateral' },
            ].map(({ key, label, desc }) => (
              <div key={key} className="flex items-center gap-3">
                <input
                  type="color"
                  value={branding[key]}
                  onChange={e => setBranding(prev => ({ ...prev, [key]: e.target.value }))}
                  className="w-10 h-10 rounded-xl border border-gray-200 cursor-pointer p-0.5"
                />
                <div className="flex-1">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{label}</span>
                  <p className="text-[10px] text-gray-400">{desc}</p>
                </div>
                <input
                  type="text"
                  value={branding[key]}
                  onChange={e => setBranding(prev => ({ ...prev, [key]: e.target.value }))}
                  className="w-24 px-2 py-1 text-xs bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg font-mono text-center"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Custom domain */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 flex items-center gap-2 mb-4">
          <FiGlobe size={16} className="text-cyan-500" /> Dominio personalizado
        </h3>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={branding.customDomain}
              onChange={e => setBranding(prev => ({ ...prev, customDomain: e.target.value }))}
              placeholder="app.miempresa.com"
              className="w-full px-4 py-2.5 text-sm bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
            <p className="text-[10px] text-gray-400 mt-1.5">
              Configura un CNAME apuntando a hingeniasuite.com. Contacta soporte para activar SSL.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceBranding;
